import "./SurveyHistoricalTable.scss";

import { Button, Flex, Text, Tooltip } from "@fluentui/react-northstar";
import { TeamsSurvey } from "@teams/types";
import { ScheduleDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useUserInfo } from "../../../_shared/actions/useUserInfo";
import { AlertError } from "../../../_shared/components/AlertError";
import { PollyIcon } from "../../../_shared/components/PollyIcon";
import { TeamsLoader } from "../../../_shared/components/TeamsLoader";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { startTaskDeleteConfirmation } from "../../../_shared/utils/startTaskDeleteConfirmation";
import { getPageContext } from "../../../_shared/utils/teamsHelpers";
import {
  getFreeUserResultsLimitText,
  isSurveyResultsRestrictedForUser,
} from "../../../_shared/utils/userHelpers";
import { useHistoricalSurveys } from "../../../agile/actions/useHistoricalSurveys";

type SurveyHistoricalTableParams = {
  series: ScheduleDTO;
};

export const SurveyHistoricalTable: React.FC<SurveyHistoricalTableParams> = ({ series }) => {
  const { teamsContext } = useTeamsContext();
  const [pageIndex, setPageIndex] = useState<number>(0);
  const { user } = useUserInfo();
  const {
    error,
    loadHistoricalSurveys,
    isHistoricalSurveysLoading,
    historicalSurveys,
    historicalSurveyTotal,
    getHistoricalSurveyLoadCount,
  } = useHistoricalSurveys();

  const loadHistoricalSurveysCallback = useCallback(() => {
    if (teamsContext) {
      loadHistoricalSurveys(teamsContext, series.id, { includeResponses: true, pageIndex });
    }
  }, [teamsContext, pageIndex, loadHistoricalSurveys, series]);

  useEffect(() => loadHistoricalSurveysCallback(), [loadHistoricalSurveysCallback]);

  if (error) {
    return <AlertError error={error} onClick={() => loadHistoricalSurveysCallback()} />;
  }

  historicalSurveys.sort((a, b) => {
    if (!b.closeAt || !a.closeAt) return 0;

    return b.closeAt.getTime() - a.closeAt.getTime();
  });

  const onDeleteInstance = (survey: TeamsSurvey) => {
    startTaskDeleteConfirmation(
      null,
      { id: survey.id },
      "Delete Instance",
      series.applicationType,
      (_err, _result) => {
        loadHistoricalSurveysCallback();
      }
    );
  };

  return (
    <div className="survey-historical-table">
      <TeamsLoader
        isLoading={isHistoricalSurveysLoading && pageIndex === 0}
        loadingMessage="Loading history"
      >
        <table className="survey-historical-table__table">
          <thead>
            <tr>
              <th className="survey-historical-table__header-cell">Send Date</th>
              <th className="survey-historical-table__header-cell">Completions</th>
            </tr>
          </thead>
          <tbody className={`survey-historical-table__body`}>
            {historicalSurveys.map(survey => {
              return (
                <tr key={survey.id} className="survey-historical-table__row">
                  <td className="survey-historical-table__cell">
                    {!isSurveyResultsRestrictedForUser(user, survey) ? (
                      <Link
                        className="survey-historical-table__link"
                        to={`${getPageContext()}/content/polly/survey/${survey.id}`}
                      >
                        {moment(survey.closeAt).format("LL")}
                      </Link>
                    ) : (
                      <Tooltip
                        content={getFreeUserResultsLimitText(user)}
                        trigger={
                          <Text weight="bold" content={moment(survey.closeAt).format("LL")} />
                        }
                      ></Tooltip>
                    )}
                  </td>
                  <td className="survey-historical-table__cell">
                    <Text weight="bold" content={survey.responseCount} />
                  </td>

                  <td className="survey-historical-table__cell survey-historical-table__action-cell">
                    <span
                      onClick={() => onDeleteInstance(survey as TeamsSurvey)}
                      className="survey-historical-table__action"
                    >
                      <PollyIcon icon="trash-icon" />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {isHistoricalSurveysLoading && (
          <Flex className="survey-historical-table__load-more">
            <Button
              data-tname="load-more-history"
              icon={<PollyIcon icon="agile-load-table-icon" />}
              fluid
              disabled={true}
              content="Loading..."
            />
          </Flex>
        )}
        {!isHistoricalSurveysLoading && historicalSurveyTotal > historicalSurveys.length && (
          <Flex className="survey-historical-table__load-more">
            <Button
              data-tname="load-more-history"
              icon={<PollyIcon icon="agile-load-table-icon" />}
              fluid
              onClick={() => {
                setPageIndex(pageIndex + 1);
              }}
              content={`Load ${getHistoricalSurveyLoadCount(pageIndex)} more`}
            />
          </Flex>
        )}
      </TeamsLoader>
    </div>
  );
};
